import { deleteField, Timestamp } from 'firebase/firestore';

import dayjs, { Dayjs } from 'dayjs';

import { HANDICAP_LABELS } from 'shared/dist/constants/handicapLabels';
import { formatHandicapIndex, parseGhinHandicapIndex } from 'shared/dist/utils/ghinIndex';

import Event from '../types/Event';

import { parseBooleanFlexible, parseDurationFlexible, parseTeamComposition } from './utilsUploads';

/**
 * Recursively removes keys with undefined values before writing to Firestore.
 * Firestore rejects documents containing undefined — optional typed fields produce
 * undefined when not set, so this must be called before setDoc.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function stripUndefined<T extends Record<string, any>>(obj: T): T {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const result: any = {};
  for (const [key, value] of Object.entries(obj)) {
    if (value === undefined) continue;
    if (Array.isArray(value)) {
      result[key] = value.map((item) =>
        item !== null && typeof item === 'object' && !Array.isArray(item) && !(item instanceof Timestamp)
          ? stripUndefined(item) : item
      );
    } else if (value !== null && typeof value === 'object' && !(value instanceof Timestamp)) {
      result[key] = stripUndefined(value);
    } else {
      result[key] = value;
    }
  }
  return result;
}

/**
 * Converts top-level undefined values to deleteField() for use with updateDoc.
 * Nested objects (including array elements) have undefined stripped instead,
 * since deleteField() cannot be used inside arrays or nested maps.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function replaceUndefinedWithDeleteField<T extends Record<string, any>>(obj: T): T {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const result: any = {};
  for (const [key, value] of Object.entries(obj)) {
    if (value === undefined) {
      result[key] = deleteField();
    } else if (Array.isArray(value)) {
      result[key] = value.map((item) =>
        item !== null && typeof item === 'object' && !Array.isArray(item) && !(item instanceof Timestamp)
          ? stripUndefined(item) : item
      );
    } else if (value !== null && typeof value === 'object' && !(value instanceof Timestamp)) {
      result[key] = stripUndefined(value);
    } else {
      result[key] = value;
    }
  }
  return result;
}

/** Whether a string can be a single Firestore document ID — e.g. a profile ID typed into the God
 *  impersonate field. Rejects anything with a slash or whitespace (a pasted URL), and the reserved
 *  "." / "..". Profile IDs are auth uids or auto-IDs, so this is deliberately strict. */
export function isValidDocumentId(id: string): boolean {
  return /^[A-Za-z0-9_-]{1,128}$/.test(id);
}

export function generateFirestoreId(): string {
  const CHARS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  const randomBytes = new Uint8Array(20);
  crypto.getRandomValues(randomBytes);

  let autoId = '';
  for (let i = 0; i < 20; i++) {
    autoId += CHARS.charAt(randomBytes[i] % CHARS.length);
  }
  return autoId;
}

/* eslint-disable @typescript-eslint/no-explicit-any */
export function convertStringToType<T extends Record<string, any>>(value: string, key: string, obj: T, specificTime?: string): any {
  // Handle array paths like 'days[0].datetime'
  if (key.includes('[') && key.includes(']')) {
    const [arrayName, indexStr, fieldName] = key.match(/(\w+)\[(\d+)\]\.(\w+)/) ?? [];
    if (arrayName && indexStr && fieldName) {
      const index = parseInt(indexStr);
      // Create array if it doesn't exist
      (obj as any)[arrayName] ??= [];
      // Create object at index if it doesn't exist
      (obj as any)[arrayName][index] ??= {};
      // Convert the value based on the field type
      return convertBasicStringToType(value, fieldName, specificTime);
    }
  }
  /* eslint-enable @typescript-eslint/no-explicit-any */

  // Handle basic types
  return convertBasicStringToType(value, key, specificTime);
}

/* eslint-disable @typescript-eslint/no-explicit-any */
function convertBasicStringToType(value: string, key: string, specificTime?: string): any {
  /* eslint-enable @typescript-eslint/no-explicit-any */
  // TODO: This is storing as local time. We are going to want to be smarter about timezones.
  if (key.includes('datetime') || key.includes('At') || key.includes('Datetime')) {
    const date = new Date(value);
    if (specificTime) {
      const [hours, minutes, seconds] = specificTime.split(':').map(Number);
      date.setHours(hours, minutes, seconds, 0);
    }
    return Timestamp.fromDate(date);
  }
  if (key.startsWith('is')) {
    return parseBooleanFlexible(value, key);
  }
  if (key.includes('memberGuest')) {
    return parseTeamComposition(value);
  }
  if (key === 'duration') {
    const minutes = parseDurationFlexible(value);
    return minutes ?? (parseInt(value) || 0);
  }
  if (typeof value === 'string' && /^\d+$/.test(value)) {
    return parseInt(value);
  }
  return value;
}

// determine if a timestamp.now() is between 11:00pm and 12:00am
// if so, return true, else return false
export function IsEndOfDay(timestamp: Timestamp, timezone?: string): boolean {
  const tz = timezone ?? Intl.DateTimeFormat().resolvedOptions().timeZone;
  const date = new Date(timestamp.seconds * 1000);
  const localHour = date.toLocaleString('en-US', {
    hour: 'numeric',
    hour12: false,
    timeZone: tz,
  });

  return parseInt(localHour) === 23;
}

export function IsStartOfDay(timestamp: Timestamp, timezone?: string): boolean {
  const tz = timezone ?? Intl.DateTimeFormat().resolvedOptions().timeZone;
  const date = new Date(timestamp.seconds * 1000);
  const localHour = date.toLocaleString('en-US', {
    hour: 'numeric',
    hour12: false,
    timeZone: tz,
  });

  return parseInt(localHour) === 0;
}

export function IsWithin24Hours(timestamp: Timestamp): boolean {
  const now = new Date();
  const target = new Date(timestamp.seconds * 1000);

  const diffInHours = (target.getTime() - now.getTime()) / (1000 * 60 * 60);
  return diffInHours > 0 && diffInHours <= 24;
}

/**
 * Formats a Handicap ID (ghin) and optional Handicap Index for display.
 * Pass ghinIndex and/or ghinHistory when available (e.g. from PersonIdentity).
 * For invitations, pass only ghin — the index portion is omitted.
 * God users also see the Handicap ID; others see only the Handicap Index.
 */
export function FormatGhin(
  ghin?: string | null,
  ghinIndex?: number | string | null,
  ghinHistory?: { index: string }[] | null,
  isGod?: boolean
): string {
  if (!ghin || ghin === '' || ghin === '0') return `${HANDICAP_LABELS.INDEX_SHORT}: unknown`;
  const raw = ghinIndex ?? ghinHistory?.[ghinHistory.length - 1]?.index;
  const formatted = raw != null
    ? typeof raw === 'number'
      ? formatHandicapIndex(raw)
      : formatHandicapIndex(parseGhinHandicapIndex(String(raw)))
    : null;
  if (isGod) {
    return formatted != null
      ? `${HANDICAP_LABELS.INDEX_SHORT}: ${formatted} (${HANDICAP_LABELS.ID} ${ghin})`
      : `${HANDICAP_LABELS.ID}: ${ghin}`;
  }
  return `${HANDICAP_LABELS.INDEX_SHORT}: ${formatted ?? 'unknown'}`;
}

export function GetFirstAndLastName(fullName: string): [firstName: string, lastName: string] {
  const regex = /^(\p{L}+(?:\s+\p{L}+)*)\s+(\p{L}+(?:-\p{L}+)*)$/u;

  if (fullName) {
    const matches = fullName.trim().match(regex);
    if (matches) {
      return [matches[1], matches[2]];
    } else {
      return [fullName, ''];
    }
  } else {
    return ['', ''];
  }
}

export function GetFirstName(fullName: string): string {
  return GetFirstAndLastName(fullName)[0];
}

export function FormatPhoneNumber(phoneNumberString: string) {
  const cleaned = ('' + phoneNumberString).replace(/\D/g, '');
  const match = cleaned.match(/^(1|)?(\d{3})(\d{3})(\d{4})$/);

  if (match) {
    const intlCode = match[1] ? '+1 ' : '';
    return [intlCode, '(', match[2], ') ', match[3], '-', match[4]].join('');
  }

  return '';
}

export function IsEventUpcoming(event: Event) {
  const currentDateTime = CurrentDateTime();
  return currentDateTime < event.days[0].datetime;
}

export function IsEventInProgress(event: Event) {
  const currentDateTime = CurrentDateTime();
  return currentDateTime > event.days[0].datetime;
}

export function IsEventOver(event: Event) {
  const currentDateTime = CurrentDateTime();
  const lastDay = event.days[event.days.length - 1];
  const eventEndTime = Timestamp.fromMillis(
    (lastDay.datetime as Timestamp).toMillis() + lastDay.duration * 60 * 1000 // convert minutes to milliseconds
  );
  return currentDateTime > eventEndTime;
}

export function IsEventSignupInProgress(event: Event) {
  const currentDateTime = CurrentDateTime();
  return currentDateTime > event.signupOpensAt && currentDateTime < event.signupClosesAt;
}

export function IsEventSignupOver(event: Event) {
  const currentDateTime = CurrentDateTime();
  return currentDateTime > event.signupClosesAt;
}

// And we could add for completeness:
export function IsEventSignupUpcoming(event: Event) {
  const currentDateTime = CurrentDateTime();
  return currentDateTime < event.signupOpensAt;
}

export function DisplayDate(date: Date, tz: string, format?: string): string {
  dayjs.tz.setDefault(tz ? tz : Intl.DateTimeFormat().resolvedOptions().timeZone);
  return dayjs.tz(date, tz).format(format ? format : 'MMM D, YYYY');
}

export function DisplayDates(dates: Date[], tz: string, format?: string, isTwoLine?: boolean): string {
  const sortedDates = dates.sort((a, b) => a.valueOf() - b.valueOf());
  const startDate = sortedDates[0];
  const endDate = sortedDates[sortedDates.length - 1];

  if (dates.length === 0) {
    return '';
  } else if (dates.length === 1) {
    return DisplayDate(startDate, tz, format);
  } else {
    if (startDate.getFullYear() === endDate.getFullYear()) {
      if (startDate.getMonth() === endDate.getMonth()) {
        return (
          startDate.toLocaleString('default', { month: 'short' }) +
          ' ' +
          startDate.getDate().toString() +
          '-' +
          endDate.getDate().toString() +
          ', ' +
          endDate.getFullYear().toString()
        );
      } else {
        if (isTwoLine) {
          return (
            startDate.toLocaleString('default', { month: 'short' }) +
            ' ' +
            endDate.toLocaleString('default', { month: 'short' }) +
            '\n' +
            startDate.getDate().toString() +
            ' ' +
            endDate.getDate().toString() +
            ', ' +
            endDate.getFullYear().toString()
          );
        } else {
          return (
            startDate.toLocaleString('default', { month: 'short' }) +
            ' ' +
            startDate.getDate().toString() +
            '-' +
            endDate.toLocaleString('default', { month: 'short' }) +
            ' ' +
            endDate.getDate().toString() +
            ', ' +
            endDate.getFullYear().toString()
          );
        }
      }
    } else {
      // TODO: Handle multi-year events.
      return (
        startDate.toLocaleString('default', { month: 'short' }) +
        ' ' +
        startDate.getDate().toString() +
        ', ' +
        startDate.getFullYear().toString() +
        ' - ' +
        endDate.toLocaleString('default', { month: 'short' }) +
        ' ' +
        endDate.getDate().toString() +
        ', ' +
        endDate.getFullYear().toString()
      );
    }
    // timezone is number of hours relative to UTC. SO PST would be -8
    //return new Date(/*(*/datetime.seconds /*+ (timezone * 60 * 60))*/ * 1000)
    //  .toLocaleDateString('en-US');
  }
}

export function CombineDateJSTimeJS(date: Dayjs, time: Dayjs): Timestamp {
  const combined = date.set('hour', time.hour()).set('minute', time.minute());
  return Timestamp.fromDate(combined.toDate());
}

export function DisplayTime(datetime: Timestamp, tz?: string, format?: string): string {
  // console.log('DisplayTime', datetime, tz, format);
  const timezone = tz ?? Intl.DateTimeFormat().resolvedOptions().timeZone;
  dayjs.tz.setDefault(timezone);

  return dayjs.tz(datetime.toDate()).format(format ?? 'h:mm A');
}

export function DisplayDateTime(datetime: Timestamp, tz: string): string {
  return DisplayDate(datetime.toDate(), tz) + ' ' + DisplayTime(datetime, tz);
}

/**
 * Shared GratiSection header title for a day-scoped section — tee sheet, scores, and per-game
 * results all render "{date} - {time}[, {optionalTitle}]" so the three tabs read as one
 * consistent style instead of each section inventing its own header format.
 */
export function DisplaySectionTitle(datetime: Timestamp, tz: string, optionalTitle?: string): string {
  const dateTime = `${DisplayDate(datetime.toDate(), tz, 'MMM D')} - ${DisplayTime(datetime, tz)}`;
  return optionalTitle ? `${dateTime}, ${optionalTitle}` : dateTime;
}

export function CurrentDateTime(): Timestamp {
  return Timestamp.now();
}

export function CurrentDate(): Timestamp {
  return Timestamp.fromDate(new Date());
}

export function FromDayJS(dayjs: Dayjs | null): Timestamp {
  return dayjs != null ? Timestamp.fromDate(dayjs.utc().toDate()) : Timestamp.now();
}

export function ToDayJS(datetime: Timestamp, timezone?: string): Dayjs {
  if (datetime === null || datetime.seconds === null) {
    datetime = Timestamp.now();
  }

  if (timezone === null) {
    timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  }

  return dayjs.tz(datetime.toDate(), timezone);
}

export function HoursMinutesToMilliseconds(hours: number, minutes: number): number {
  return hours * 60 * 60 * 1000 + minutes * 60 * 1000;
}

export function TimezoneList(): { value: number; label: string }[] {
  return [
    { value: -8, label: 'PST' },
    { value: -7, label: 'PDT' },
    { value: -6, label: 'MDT' },
  ];
}

export const FormatShortDate = (date: Date): string => {
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

export const ReformatGhinDate = (date: string, prefix?: string, suffix?: string): string => {
  const year = date.substring(0, 4);
  const month = date.substring(4, 6);
  const day = date.substring(6, 8);

  const dateObj = new Date(`${year}-${month}-${day}`);
  const formattedDate = dateObj.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    timeZone: 'UTC',
  });

  return `${prefix}${formattedDate}${suffix}`;
};

// Use a fixed non-leap year for month/day conversion
const FIXED_YEAR = 2023;

/**
 * Convert MonthDay to Dayjs
 */
export function monthDayToDayjs(monthDay: { month: number; day: number }): Dayjs {
  return dayjs(`${FIXED_YEAR}-${monthDay.month}-${monthDay.day}`);
}

/**
 * Convert Dayjs to MonthDay
 */
export function dayjsToMonthDay(date: Dayjs | null): { month: number; day: number } | null {
  if (!date || !date.isValid()) return null;
  return { month: date.month() + 1, day: date.date() };
}

/**
 * Check if a year is a leap year
 */
export function isLeapYear(year: number): boolean {
  return (year % 4 === 0 && year % 100 !== 0) || (year % 400 === 0);
}

/**
 * Create a Timestamp from a MonthDay and year.
 * Handles leap year edge case for Feb 29 - uses Feb 28 if not a leap year.
 */
export function createTimestampFromMonthDay(monthDay: { month: number; day: number }, year: number): Timestamp {
  const { month } = monthDay;
  let { day } = monthDay;

  // Handle Feb 29 in non-leap years
  if (month === 2 && day === 29 && !isLeapYear(year)) {
    day = 28;
  }

  // Month is 0-based in JavaScript Date, so subtract 1
  return Timestamp.fromDate(new Date(year, month - 1, day));
}

export const formatEnumLabel = (value: string): string =>
  value.charAt(0).toUpperCase() + value.slice(1).toLowerCase().replace(/_/g, ' ');
